import { Inject, Injectable, signal } from '@angular/core'
import maplibregl, { GeoJSONSource, LngLat, Map, MapGeoJSONFeature } from 'maplibre-gl'
import { XyzTileModel } from '../../shared/models/xyz-tile.model'
import { Subject } from 'rxjs'
import { FeatureCollection } from 'geojson'
import * as turf from '@turf/turf'
import { MapIcons } from '../enums/map-icons.enum'
import { GeocodingControl } from '@maptiler/geocoding-control/maplibregl'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from '../models'
import { Layers } from 'src/app/shared/layers-config/layers'
import { BBox } from 'geojson'
import { SphericalMercator } from '@mapbox/sphericalmercator'
import { Theme, ThemeMode } from '../../shared/services/theme.service'

@Injectable({
  providedIn: 'root',
})
export class MapService {
  private readonly themeModeStorageKey = 'app-theme-mode'
  private sphericalMercator: SphericalMercator
  private currentXyzTiles: XyzTileModel[] = []
  private currentCenter: LngLat = new LngLat(0, 0)
  private currentZoom: number | undefined
  private readonly systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)')
  public mapViewChanged = new Subject<XyzTileModel[]>()
  public mapReadyChanged = new Subject<void>()
  public isMapReady = false
  public readonly isSatelliteEnabled = signal(false)
  public selectedTheme = (window.localStorage.getItem('theme') as Theme) ?? 'dark'

  public gc = new GeocodingControl({
    apiKey: this.config.maptiler_key,
    marker: false,
    limit: 4,
    minLength: 2,
    fuzzyMatch: false,
    debounceSearch: 0,
    language: ['de', 'en'],
    collapsed: false,
    noResultsMessage: 'No matching location found. Format: lat/lon always 3 decimals',
    errorMessage: 'No matching location found. Format: lat/lon always 3 decimals',
    types: ['neighbourhood', 'place', 'postal_code', 'poi'],
    excludeTypes: true,
  })
  public markerClassName = ''
  public el = document.createElement('div')
  public marker = new maplibregl.Marker({
    anchor: 'bottom',
    element: this.el,
    draggable: false,
  })

  public map!: Map
  private readonly images = [
    { uri: 'assets/images/accident.png', id: MapIcons.VA },
    { uri: 'assets/images/bdv.png', id: MapIcons.BDV },
    { uri: 'assets/images/tl.png', id: MapIcons.TL },
    { uri: 'assets/images/cs.png', id: MapIcons.CS },
    { uri: 'assets/images/lv.png', id: MapIcons.LV },
    { uri: 'assets/images/heavy-rain.png', id: MapIcons.HR },
    { uri: 'assets/images/heavy-snow.png', id: MapIcons.HS },
    { uri: 'assets/images/strong-wind.png', id: MapIcons.SW },
    { uri: 'assets/images/pr.png', id: MapIcons.PR },
    { uri: 'assets/images/wwd.png', id: MapIcons.WWD },
    { uri: 'assets/images/ior.png', id: MapIcons.IOR },
    { uri: 'assets/images/al.png', id: MapIcons.AL },
    { uri: 'assets/images/bl.png', id: MapIcons.BL },
    { uri: 'assets/images/sv.png', id: MapIcons.SV },
    { uri: 'assets/images/tjt.png', id: MapIcons.TJT },
    { uri: 'assets/images/accident-test.png', id: MapIcons.TVA },
    { uri: 'assets/images/bdv-test.png', id: MapIcons.TBDV },
    { uri: 'assets/images/tl-test.png', id: MapIcons.TTL },
    { uri: 'assets/images/cs-test.png', id: MapIcons.TCS },
    { uri: 'assets/images/lv-test.png', id: MapIcons.TLV },
    { uri: 'assets/images/heavy-rain-test.png', id: MapIcons.THR },
    { uri: 'assets/images/heavy-snow-test.png', id: MapIcons.THS },
    { uri: 'assets/images/strong-wind-test.png', id: MapIcons.TSW },
    { uri: 'assets/images/pr-test.png', id: MapIcons.TPR },
    { uri: 'assets/images/wwd-test.png', id: MapIcons.TWWD },
    { uri: 'assets/images/ior-test.png', id: MapIcons.TIOR },
    { uri: 'assets/images/al-test.png', id: MapIcons.TAL },
    { uri: 'assets/images/bl-test.png', id: MapIcons.TBL },
    { uri: 'assets/images/sv-test.png', id: MapIcons.TSV },
    { uri: 'assets/images/tjt-test.png', id: MapIcons.TTJT },
    { uri: 'assets/images/camera.png', id: MapIcons.WC },
    { uri: 'assets/images/keyboard-arrow-left.png', id: 'arrow' },
    { uri: 'assets/images/keyboard-arrow-right.png', id: 'arrow-against' },
    { uri: 'assets/images/arrows-left-right.png', id: 'arrow-both' },
  ]

  public getImageUriById(imageId: string): string {
    return this.images.find((img) => img.id === imageId)?.uri ?? ''
  }

  constructor(@Inject(APP_CONFIG) private readonly config: AppConfigModel) {
    this.sphericalMercator = new SphericalMercator({ size: 256 })
    this.systemThemeQuery.addEventListener('change', this.onSystemThemeChanged)
  }

  private readonly onSystemThemeChanged = (): void => {
    if (this.getThemeMode() !== 'system' || this.isSatelliteEnabled()) {
      return
    }

    this.selectedTheme = this.getSystemTheme()
    window.localStorage.setItem('theme', this.selectedTheme)
    this.applyMapThemeFromSelection()
  }

  private calcXyzTiles(minZoom?: number, maxZoom?: number): XyzTileModel[] {
    const xyzTiles: XyzTileModel[] = []

    if (this.map) {
      let zoom = Math.floor(this.map.getZoom())
      // if there is a max zoom provided don't return tiles for larger zoom levels
      zoom = maxZoom !== undefined ? Math.min(zoom, maxZoom) : zoom
      zoom = minZoom !== undefined ? Math.max(zoom, minZoom) : zoom
      const tiles = this.sphericalMercator.xyz(
        [
          this.map.getBounds().getWest(),
          this.map.getBounds().getSouth(),
          this.map.getBounds().getEast(),
          this.map.getBounds().getNorth(),
        ],
        zoom,
      )
      for (let x = tiles.minX; x <= tiles.maxX; x++) {
        for (let y = tiles.minY; y <= tiles.maxY; y++) {
          xyzTiles.push({
            x: x,
            y: y,
            z: zoom,
          })
        }
      }
    }

    return xyzTiles
  }

  public initMap(map: Map) {
    window.map = map
    this.map = map
    this.map.on('moveend', () => {
      this.updateMapTiles()
    })
    this.map.on('zoomend', () => {
      this.updateMapTiles()
    })
    this.map.on('load', async () => {
      if (this.map.loaded()) {
        await this.loadImages()
        this.isMapReady = true
        this.applyMapThemeFromSelection()
        this.updateMapTiles(false)
        this.mapReadyChanged.next()
      }
    })
  }

  public getXyzTiles(maxZoom?: number, minZoom?: number) {
    if (minZoom && maxZoom) {
      return this.calcXyzTiles(minZoom, maxZoom)
    } else if (maxZoom) {
      return this.calcXyzTiles(maxZoom)
    }
    return this.currentXyzTiles
  }

  public setPosition(lat?: number, lon?: number, zoom?: number) {
    if (lat && lon && this.currentCenter.lat != lat && this.currentCenter.lng != lon) {
      this.currentCenter = new LngLat(lon, lat)
      this.map.setCenter(this.currentCenter)
    }
    if (zoom && this.currentZoom != zoom) {
      this.currentZoom = zoom
      this.map.setZoom(this.currentZoom)
    }
  }

  public setLayerVisibility(layerId: string, isVisible: boolean) {
    this.map.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none')
  }

  public getLayerVisibilty(layerId: string) {
    const layer = this.map.getLayer(layerId)
    if (layer && layer.visibility === 'visible') {
      return true
    }
    return false
  }

  public updateMapTiles(emitEvent = true): void {
    this.currentXyzTiles = this.calcXyzTiles()
    this.currentCenter = this.map.getCenter()
    this.currentZoom = this.map.getZoom()
    if (emitEvent) {
      this.mapViewChanged.next(this.currentXyzTiles)
    }

    // if (!isEqual(this.currentXyzTiles, xyzTiles) || forceUpdate) {
    //   this.currentXyzTiles = xyzTiles
    //   this.xyzTileSubject.next(xyzTiles)
    // }
  }

  public setGeoJsonData(sourceId: string, featureCollection: FeatureCollection) {
    const source: GeoJSONSource | undefined = this.map.getSource(sourceId) as GeoJSONSource
    if (source) {
      source.setData(featureCollection)
    }
  }

  public getFeatures(layerId: string, bbox: BBox): MapGeoJSONFeature[] {
    const coordSw = this.map.project([bbox[0], bbox[1]])
    const coordNe = this.map.project([bbox[2], bbox[3]])

    const features = this.map.queryRenderedFeatures([coordSw, coordNe], {
      layers: [layerId],
    })

    if (features && features.length > 0) {
      return features.filter(
        (obj, index) => features.findIndex((item) => item.id === obj.id) === index,
      )
    }
    return []
  }

  private async loadImages(): Promise<any> {
    for (const image of this.images) {
      const imageToAdd = await this.map.loadImage(image.uri)
      if (imageToAdd) {
        this.map.addImage(image.id, imageToAdd.data)
      }
    }
  }

  public activateGeoLocationSearch() {
    this.map.addControl(this.gc, 'top-left')

    // Listen for when a location is selected (including Enter key on first result)
    this.gc.on('pick', (event: any) => {
      if (event && event.center) {
        const [lng, lat] = event.center
        // Fly to the selected location with zoom level 15
        this.map.flyTo({
          center: [lng, lat],
          zoom: 15,
          essential: true,
          duration: 1000,
        })
      }
    })

    return undefined
  }

  public deactivateGeoLocationSearch() {
    this.map.removeControl(this.gc)
    return undefined
  }

  public setMarker(coordinates: LngLat, className: string) {
    this.markerClassName ? this.marker.removeClassName(this.markerClassName) : null
    this.markerClassName = className
    this.marker.addClassName(this.markerClassName)
    this.marker.setLngLat(coordinates).addTo(this.map)
  }

  public changeMarker(className: string) {
    try {
      this.marker ? this.marker.removeClassName(this.markerClassName) : null
      this.markerClassName = className
      this.marker.addClassName(this.markerClassName)
    } catch (e) {}
  }

  public removeMarker() {
    try {
      this.marker.remove()
    } catch (e) {}
  }

  public switchSatelliteView() {
    this.isSatelliteEnabled.update((enabled) => !enabled)

    if (this.isSatelliteEnabled()) {
      this.changeMapStyle(`${this.config.map_style_urls.satellite}`)
      return
    }

    this.applyMapThemeFromSelection()
  }

  public applyMapThemeFromSelection(): void {
    const themeMode = this.getThemeMode()
    this.selectedTheme = themeMode === 'system' ? this.getSystemTheme() : themeMode

    window.localStorage.setItem('theme', this.selectedTheme)

    if (this.isSatelliteEnabled() || !this.isMapReady) {
      return
    }

    this.changeMapStyle(
      `${this.config.map_style_urls[this.selectedTheme]}?key=${this.config.maptiler_key}`,
    )
  }

  private getThemeMode(): ThemeMode {
    const savedThemeMode = window.localStorage.getItem(this.themeModeStorageKey)

    if (savedThemeMode === 'system' || savedThemeMode === 'light' || savedThemeMode === 'dark') {
      return savedThemeMode
    }

    return 'system'
  }

  private getSystemTheme(): Theme {
    return this.systemThemeQuery.matches ? 'dark' : 'light'
  }

  public changeMapStyle(style: string) {
    const layerCfg = {
      transformStyle: (previousStyle: any, nextStyle: any) => {
        var custom_layers = previousStyle
          ? previousStyle.layers.filter((layer: any) => {
              let layers = Object.values(Layers).concat([
                { name: 'obstacle' },
                { name: 'mobile' },
                { name: 'roadCondition' },
                { name: 'customer' },
              ])
              return layers.find((entry) => {
                return layer.id.substring(0, entry.name.length).includes(entry.name)
              })
            })
          : []
        var layers = nextStyle.layers.concat(custom_layers)
        var sources = nextStyle.sources
        if (previousStyle) {
          for (const [key, value] of Object.entries(previousStyle.sources)) {
            if (key) {
              sources[key] = value
            }
          }
        }
        return {
          ...nextStyle,
          sources: sources,
          layers: layers,
        }
      },
    }
    this.map.setStyle(style, layerCfg)
  }
}
